import { useFeatureFlag } from '@/composables/useFeatureFlag'
import { FlagEnum } from '@/types/generated'
import VoiceBotFeedback from '@/views/VoiceBotFeedbackView.vue'
import type { RouteRecordRaw } from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'

import SettingsBase from '@/components/app/settings/SettingsBase.vue'
import { ROUTE_BASES, ROUTES } from '@/router/routes'
import CallDetailView from '@/views/app/calls/CallDetailView.vue'
import CallListView from '@/views/app/calls/CallListView.vue'
import CallListViewImproved from '@/views/app/calls/CallListViewImproved.vue'
import ActivityListView from '@/views/app/activities/ActivityListView.vue'
import CaseCreateView from '@/views/app/cases/CaseCreateView.vue'
import CaseDetailAiChatView from '@/views/app/cases/case-detail/CaseDetailAiChatView.vue'
import CaseDetailAppointmentsView from '@/views/app/cases/case-detail/CaseDetailAppointmentsView.vue'
import CaseDetailBaseView from '@/views/app/cases/case-detail/CaseDetailBaseView.vue'
import CaseDetailCommunicationView from '@/views/app/cases/case-detail/CaseDetailCommunicationView.vue'
import CaseDetailEntitiesView from '@/views/app/cases/case-detail/CaseDetailEntitiesView.vue'
import CaseDetailLawSpecificDataView from '@/views/app/cases/case-detail/CaseDetailLawSpecificDataView.vue'
import CaseDetailNotesView from '@/views/app/cases/case-detail/CaseDetailNotesView.vue'
import CaseDetailOverviewView from '@/views/app/cases/case-detail/CaseDetailOverviewView.vue'
import CaseDetailTimelineView from '@/views/app/cases/case-detail/CaseDetailTimelineView.vue'
import CaseMergeIntoView from '@/views/app/cases/case-detail/CaseMergeIntoView.vue'
import GlobalChatView from '@/views/app/global-chat/GlobalChatView.vue'
import AiAssistantChatView from '@/views/app/ai-assistant/AiAssistantChatView.vue'
import AiBaseSettingsView from '@/views/app/settings/ai/AiBaseSettingsView.vue'
import PromptTemplateDetailSettingsView from '@/views/app/settings/ai/PromptTemplateDetailSettingsView.vue'
import PromptTemplateSettingsView from '@/views/app/settings/ai/PromptTemplateSettingsView.vue'
import WordDocumentGenerationTemplateDetailSettingsView from '@/views/app/settings/ai/WordDocumentGenerationTemplateDetailSettingsView.vue'
import WordDocumentGenerationTemplateSettingsView from '@/views/app/settings/ai/WordDocumentGenerationTemplateSettingsView.vue'
import AppointmentCalendarListSettingsView from '@/views/app/settings/appointments/AppointmentCalendarListSettingsView.vue'
import AppointmentsBaseSettingsView from '@/views/app/settings/appointments/AppointmentsBaseSettingsView.vue'
import AppointmentsCalendarDetailSettingsView from '@/views/app/settings/appointments/AppointmentsCalendarDetailSettingsView.vue'
import AppointmentsEmbedSettingsView from '@/views/app/settings/appointments/AppointmentsEmbedSettingsView.vue'
import AppointmentsLinksSettingsView from '@/views/app/settings/appointments/AppointmentsLinksSettingsView.vue'
import AppointmentsServiceDetailSettingsView from '@/views/app/settings/appointments/AppointmentsServiceDetailSettingsView.vue'
import AppointmentsServiceListSettingsView from '@/views/app/settings/appointments/AppointmentsServiceListSettingsView.vue'
import AppointmentsSetupSettingsView from '@/views/app/settings/appointments/AppointmentsSetupSettingsView.vue'
import DeliveryStatusSettingsView from '@/views/app/settings/email/DeliveryStatusSettingsView.vue'
import DesignSettingsView from '@/views/app/settings/email/DesignSettingsView.vue'
import JupusAddressSettingsView from '@/views/app/settings/email/JupusAddressSettingsView.vue'
import NotificationsSettingsView from '@/views/app/settings/email/NotificationsSettingsView.vue'
import PlaceholdersSettingsView from '@/views/app/settings/email/PlaceholdersSettingsView.vue'
import SignatureSettingsView from '@/views/app/settings/email/SignatureSettingsView.vue'
import TemplateDetailSettingsView from '@/views/app/settings/email/TemplateDetailSettingsView.vue'
import TemplateSettingsView from '@/views/app/settings/email/TemplateSettingsView.vue'
import TEMPORARYCaseAiChatView from '@/views/cases/TEMPORARYCaseAiChatView.vue'
import TEMPORARYCaseEntitiesView from '@/views/cases/TEMPORARYCaseEntitiesView.vue'
import TEMPORARYCaseLawSpecificDataView from '@/views/cases/TEMPORARYCaseLawSpecificDataView.vue'
import IntegrationsExportView from '@/views/integrations/ExportView.vue'
import { ActaportSuccessPage } from '@/components/integrations/actaport'
import { type IStaticMethods } from 'preline/preline'

import ClientPortalWrapper from '@/components/clientPortal/ClientPortalWrapper.vue'
import OfficeEditorView from '@/views/app/OfficeEditorView.vue'
import OrganizeDocumentsView from '@/views/app/cases/OrganizeDocumentsView.vue'
import AiAssistantBetaSettingsView from '@/views/app/settings/ai/AiAssistantBetaSettingsView.vue'
import AiAutonomySettingsView from '@/views/app/settings/ai/AiAutonomySettingsView.vue'
import AiEntityExtractionSettingsView from '@/views/app/settings/ai/AiEntityExtractionSettingsView.vue'
import AiNotificationsSettingsView from '@/views/app/settings/ai/AiNotificationsSettingsView.vue'
import CaseLifecycleWorkflowBasicSettingsView from '@/views/app/settings/ai/CaseLifecycleWorkflowBasicSettingsView.vue'
import CaseLifecycleWorkflowSettingsView from '@/views/app/settings/ai/CaseLifecycleWorkflowSettingsView.vue'
import PhoneForwardingRuleEditSettingsView from '@/views/app/settings/ai/PhoneForwardingRuleEditSettingsView.vue'
import PhoneForwardingRuleSettingsView from '@/views/app/settings/ai/PhoneForwardingRuleSettingsView.vue'
import PhoneNumberDetailSettingsView from '@/views/app/settings/ai/PhoneNumberDetailSettingsView.vue'
import PhoneNumberSettingsView from '@/views/app/settings/ai/PhoneNumberSettingsView.vue'
import DocumentTemplatesView from '@/views/app/settings/documentTemplates/DocumentTemplatesView.vue'
import ActaportSettingsView from '@/views/app/settings/law-office-software/ActaportSettingsView.vue'
import AdvoluxSettingsView from '@/views/app/settings/law-office-software/AdvoluxSettingsView.vue'
import AdvowareSettingsView from '@/views/app/settings/law-office-software/AdvowareSettingsView.vue'
import DatevSettingsView from '@/views/app/settings/law-office-software/DatevSettingsView.vue'
import JurnodesSettingsView from '@/views/app/settings/law-office-software/JurnodesSettingsView.vue'
import KleosSettingsView from '@/views/app/settings/law-office-software/KleosSettingsView.vue'
import LawfirmSettingsView from '@/views/app/settings/law-office-software/LawfirmSettingsView.vue'
import RaMicroSettingsView from '@/views/app/settings/law-office-software/RaMicroSettingsView.vue'
import KleosAuthPopup from '@/views/app/settings/law-office-software/components/KleosAuthPopup.vue'
import WebhooksSettingsView from '@/views/app/settings/integrations/WebhooksSettingsView.vue'
import EmailEgressSettingsView from '@/views/app/settings/integrations/EmailEgressSettingsView.vue'
import HubspotSettingsView from '@/views/app/settings/integrations/HubspotSettingsView.vue'
import HibpSettingsView from '@/views/app/settings/integrations/HibpSettingsView.vue'
import IustaSettingsView from '@/views/app/settings/integrations/IustaSettingsView.vue'
import UserProfileSettingsView from '@/views/app/settings/user/UserProfileSettingsView.vue'
import UserPasswordSettingsView from '@/views/app/settings/user/UserPasswordSettingsView.vue'
import PDFFormTemplateCreateView from '@/views/app/settings/pdfFormTemplates/PDFFormTemplateCreateView.vue'
import PDFFormTemplateEditView from '@/views/app/settings/pdfFormTemplates/PDFFormTemplateEditView.vue'
import QuestionnaireDetailView from '@/views/app/settings/questionnaires/QuestionnaireDetailView.vue'
import QuestionnaireListView from '@/views/app/settings/questionnaires/QuestionnaireListView.vue'
import OnboardingPackageListView from '@/views/app/settings/onboarding-packages/OnboardingPackageListView.vue'
import OnboardingPackageDetailView from '@/views/app/settings/onboarding-packages/OnboardingPackageDetailView.vue'
import TEMPORARYCaseDocumentsView from '@/views/cases/TEMPORARYCaseDocumentsView.vue'
import TEMPORARYCaseWordDocumentGeneratorView from '@/views/cases/TEMPORARYCaseWordDocumentGeneratorView.vue'
import ClientPortalExternalCompletedView from '@/views/clientPortal/ClientPortalExternalCompletedView.vue'
import ClientPortalHomeView from '@/views/clientPortal/ClientPortalHomeView.vue'
import ClientPortalPDFFormView from '@/views/clientPortal/ClientPortalPDFFormView.vue'
import ClientPortalPdfTemplateDetailView from '@/views/clientPortal/ClientPortalPDFTemplateDetailView.vue'
import ClientPortalQuestionnaireDetailView from '@/views/clientPortal/ClientPortalQuestionnaireDetailView.vue'
import ClientPortalOnboardingRedirectView from '@/views/clientPortal/ClientPortalOnboardingRedirectView.vue'
import ClientPortalQuestionnaireExternalRedirectView from '@/views/clientPortal/ClientPortalQuestionnaireExternalRedirectView.vue'
import ClientPortalQuestionnaireExternalView from '@/views/clientPortal/ClientPortalQuestionnaireExternalView.vue'
import ClientPortalQuestionnairePreviewView from '@/views/clientPortal/ClientPortalQuestionnairePreviewView.vue'
import LegacyCaseListView from '@/views/app/cases/legacy/LegacyCaseListView.vue'
import OnboardingCountdownView from '@/views/onboarding/OnboardingCountdownView.vue'
import StartView from '@/views/app/StartView.vue'
import LawFirmDataView from '@/views/app/settings/lawfirm/LawfirmDataView.vue'
import LawfirmBrandingView from '@/views/app/settings/lawfirm/LawfirmBrandingView.vue'
import LawfirmEmployeeManagementView from '@/views/app/settings/lawfirm/LawfirmEmployeeManagementView.vue'
import LawfirmEmployeeCreateView from '@/views/app/settings/lawfirm/LawfirmEmployeeCreateView.vue'
import LawfirmEmployeeEditView from '@/views/app/settings/lawfirm/LawfirmEmployeeEditView.vue'

declare global {
  interface Window {
    HSStaticMethods: IStaticMethods
  }
}

const appRoutes: RouteRecordRaw[] = [
  {
    path: ROUTES.APP.START,
    name: 'Start',
    component: StartView,
    meta: { title: 'Login', public: true }
  },
  {
    path: ROUTES.APP.OFFICE_EDITOR,
    name: 'Office Editor',
    component: OfficeEditorView,
    meta: { title: 'Office Editor' },
    beforeEnter: async (to, from, next) => {
      const officeEditingFlag = useFeatureFlag(FlagEnum.OFFICE_DOCUMENT_EDITING)
      await officeEditingFlag.checkFlag()

      if (!officeEditingFlag.isEnabled.value) {
        return next({ path: '/cases' })
      }

      next()
    }
  },
  {
    path: ROUTES.APP.CASES.LEGACY.CASE_LIST,
    name: 'Legacy Case List',
    component: LegacyCaseListView,
    meta: { title: 'Anfragen' }
  },
  {
    path: ROUTES.APP.CASES.LEGACY.CASE_DETAIL,
    name: 'Legacy Case Detail',
    component: () => import('@/views/app/cases/legacy/LegacyCaseDetailPageWrapper.vue'),
    meta: { title: 'Fallübersicht' }
  },
  {
    path: ROUTES.APP.CASES.CASE_CREATE,
    name: 'Create Case',
    component: CaseCreateView,
    meta: { title: 'Fall erstellen' }
  },
  {
    path: ROUTES.APP.CASES.ORGANIZE_PAGES,
    name: 'Organize Pages',
    component: OrganizeDocumentsView,
    meta: { title: 'Seiten organisieren' }
  },
  {
    path: ROUTES.APP.EXPORT,
    name: 'Integrations Export',
    component: IntegrationsExportView,
    meta: { title: 'Export' }
  },
  {
    path: '/app/integrations/actaport/export/success/:caseId',
    name: 'Actaport Export Success',
    component: ActaportSuccessPage,
    meta: { title: 'Export Successful' },
    props: (route) => ({
      caseId: route.params.caseId,
      result: route.query
    })
  },
  // INTERMIDIATE ROUTES, JUST SO WE CAN GET CASE SPECIFIC DATA OUT
  {
    path: ROUTES.APP.CASES.CASE_OVERVIEW,
    name: 'Case Overview',
    component: LegacyCaseListView,
    meta: { title: 'Anfragen' }
  },
  {
    path: ROUTES.APP.CASES.CASE_DETAIL.LAW_SPECIFIC_DATA,
    name: 'Case Law Specific Data',
    component: TEMPORARYCaseLawSpecificDataView,
    meta: { title: 'Rechtsspezifische Daten' }
  },
  {
    path: ROUTES.APP.CASES.CASE_DETAIL.ENTITIES,
    name: 'Entities',
    component: TEMPORARYCaseEntitiesView,
    meta: { title: 'Beteiligte' }
  },
  {
    path: ROUTES.APP.CASES.CASE_DETAIL.DOCUMENT_GENERATION,
    name: 'Case Document Generation',
    component: TEMPORARYCaseWordDocumentGeneratorView,
    meta: { title: 'Dokumentenerstellung' }
  },
  {
    path: ROUTES.APP.CASES.CASE_DETAIL.AI_CHAT,
    name: 'Case AI Chat',
    component: TEMPORARYCaseAiChatView,
    meta: { title: 'KI-Chat zur Anfrage' }
  },
  {
    path: ROUTES.APP.CASES.CASE_DETAIL.DOCUMENTS,
    name: 'Case Documents',
    component: TEMPORARYCaseDocumentsView,
    meta: { title: 'Dokumente' }
  },
  {
    path: ROUTES.APP.CASES.CASE_DETAIL.MERGE_INTO,
    name: 'Case Merge Into',
    component: CaseMergeIntoView,
    meta: { title: 'Duplikate zusammenführen' },
    beforeEnter: async (_to, _from, next) => {
      const caseUuid = _to.params.uuid
      const caseMergeIntoFlag = useFeatureFlag(FlagEnum.CASE_DUPLICATE_PREVENTION)
      await caseMergeIntoFlag.checkFlag()
      if (!caseMergeIntoFlag.isEnabled.value) {
        return next({ name: 'Legacy Case Detail', params: { uuid: caseUuid } })
      }
      next()
    }
  },
  // END OF INTERMIDIATE ROUTES
  {
    path: ROUTES.APP.CASES.CASE_DETAIL.ROOT,
    name: 'Case Detail',
    component: CaseDetailBaseView,
    meta: { title: 'Falldetails' },
    children: [
      {
        path: ROUTES.APP.CASES.CASE_DETAIL.OVERVIEW,
        component: CaseDetailOverviewView,
        meta: { title: 'Fallübersicht' }
      },
      {
        path: ROUTES.APP.CASES.CASE_DETAIL.COMMUNICATION,
        component: CaseDetailCommunicationView,
        meta: { title: 'Kommunikation' }
      },
      {
        path: ROUTES.APP.CASES.CASE_DETAIL.ENTITIES,
        component: CaseDetailEntitiesView,
        meta: { title: 'Beteiligte' }
      },
      {
        path: ROUTES.APP.CASES.CASE_DETAIL.APPOINTMENTS,
        component: CaseDetailAppointmentsView,
        meta: { title: 'Termine' }
      },
      {
        path: ROUTES.APP.CASES.CASE_DETAIL.LAW_SPECIFIC_DATA,
        component: CaseDetailLawSpecificDataView,
        meta: { title: 'Rechtsspezifische Daten' }
      },
      {
        path: ROUTES.APP.CASES.CASE_DETAIL.TIMELINE,
        component: CaseDetailTimelineView,
        meta: { title: 'Zeitleiste' }
      },
      {
        path: ROUTES.APP.CASES.CASE_DETAIL.AI_CHAT,
        component: CaseDetailAiChatView,
        meta: { title: 'KI-Chat zur Anfrage' }
      },
      {
        path: ROUTES.APP.CASES.CASE_DETAIL.NOTES,
        component: CaseDetailNotesView,
        meta: { title: 'Interne Notizen' }
      }
      // {
      //   path: ROUTES.APP.CASES.CASE_DETAIL.DOCUMENT_GENERATION,
      //   component: CaseDocumentGenerationView,
      //   meta: { title: 'Dokumentenerstellung' }
      // }
    ]
  },
  {
    path: ROUTES.APP.SETTINGS.ROOT,
    name: 'Settings',
    component: SettingsBase,
    meta: { title: 'Einstellungen' },
    children: [
      // Email settings
      {
        path: ROUTES.APP.SETTINGS.EMAILS.JUPUS_EMAIL,
        component: JupusAddressSettingsView,
        meta: { title: 'Einstellungen > JUPUS E-Mail Adresse' }
      },
      {
        path: ROUTES.APP.SETTINGS.QUESTIONNAIRES.QUESTIONNAIRE_LIST,
        component: QuestionnaireListView,
        meta: { title: 'Einstellungen > Fragebögen' }
      },
      {
        path: ROUTES.APP.SETTINGS.CLIENT_ONBOARDING.ONBOARDING_PACKAGES,
        component: OnboardingPackageListView,
        meta: { title: 'Einstellungen > Onboarding-Pakete' }
      },
      {
        path: ROUTES.APP.SETTINGS.CLIENT_ONBOARDING.DOCUMENT_TEMPLATES,
        component: DocumentTemplatesView,
        meta: { title: 'Einstellungen > Dokumentvorlagen' }
      },
      {
        path: ROUTES.APP.SETTINGS.CLIENT_ONBOARDING.QUESTIONNAIRES,
        component: QuestionnaireListView,
        meta: { title: 'Einstellungen > Fragebögen' }
      },
      {
        path: ROUTES.APP.SETTINGS.EMAILS.TEMPLATES,
        component: TemplateSettingsView,
        meta: { title: 'Einstellungen > E-Mail-Vorlagen' }
      },
      {
        path: ROUTES.APP.SETTINGS.EMAILS.TEMPLATE_DETAIL,
        component: TemplateDetailSettingsView,
        meta: { title: 'Einstellungen > E-Mail-Vorlagendetails' }
      },
      {
        path: ROUTES.APP.SETTINGS.EMAILS.DESIGN,
        component: DesignSettingsView,
        meta: { title: 'Einstellungen > E-Mail-Design' }
      },
      {
        path: ROUTES.APP.SETTINGS.EMAILS.SIGNATURES,
        component: SignatureSettingsView,
        meta: { title: 'Einstellungen > E-Mail-Signaturen' }
      },
      {
        path: ROUTES.APP.SETTINGS.EMAILS.NOTIFICATIONS,
        component: NotificationsSettingsView,
        meta: { title: 'Einstellungen > Benachrichtigungen' }
      },
      {
        path: ROUTES.APP.SETTINGS.EMAILS.PLACEHOLDERS,
        component: PlaceholdersSettingsView,
        meta: { title: 'Einstellungen > Platzhalter' }
      },
      {
        path: ROUTES.APP.SETTINGS.EMAILS.DELIVERY_STATUS,
        component: DeliveryStatusSettingsView,
        meta: { title: 'Einstellungen > Versandstatus' }
      },
      // Appointments settings
      {
        path: ROUTES.APP.SETTINGS.APPOINTMENTS.SETUP,
        component: AppointmentsSetupSettingsView,
        meta: { title: 'Einstellungen > Termineinrichtung' }
      },
      {
        path: ROUTES.APP.SETTINGS.APPOINTMENTS.CALENDAR_LIST,
        component: AppointmentCalendarListSettingsView,
        meta: { title: 'Einstellungen > Kalenderübersicht' }
      },
      {
        path: ROUTES.APP.SETTINGS.APPOINTMENTS.CALENDAR_DETAIL,
        component: AppointmentsCalendarDetailSettingsView,
        meta: { title: 'Einstellungen > Kalenderdetails' }
      },
      {
        path: ROUTES.APP.SETTINGS.APPOINTMENTS.SERVICE_LIST,
        component: AppointmentsServiceListSettingsView,
        meta: { title: 'Einstellungen > Dienstleistungen' }
      },
      {
        path: ROUTES.APP.SETTINGS.APPOINTMENTS.SERVICE_DETAIL,
        component: AppointmentsServiceDetailSettingsView,
        meta: { title: 'Einstellungen > Dienstleistungsdetails' }
      },
      {
        path: ROUTES.APP.SETTINGS.APPOINTMENTS.BASE_SETTINGS,
        component: AppointmentsBaseSettingsView,
        meta: { title: 'Einstellungen > Grundeinstellungen' }
      },
      {
        path: ROUTES.APP.SETTINGS.APPOINTMENTS.LINKS,
        component: AppointmentsLinksSettingsView,
        meta: { title: 'Einstellungen > Terminbuchungslinks' }
      },
      {
        path: ROUTES.APP.SETTINGS.APPOINTMENTS.EMBED,
        component: AppointmentsEmbedSettingsView,
        meta: { title: 'Einstellungen > Terminbuchung einbetten' }
      },
      {
        path: ROUTES.APP.SETTINGS.AI.DOCUMENT_GENERATION_TEMPLATES,
        component: WordDocumentGenerationTemplateSettingsView,
        meta: { title: 'Einstellungen > Dokumentenvorlagen' }
      },
      {
        path: ROUTES.APP.SETTINGS.AI.DOCUMENT_GENERATION_TEMPLATES_BULK,
        component: () =>
          import('@/views/app/settings/ai/WordDocumentGenerationTemplateBulkUploadView.vue'),
        meta: { title: 'Einstellungen > Dokumentenvorlagen > Massenupload' }
      },
      {
        path: ROUTES.APP.SETTINGS.AI.DOCUMENT_GENERATION_TEMPLATE_DETAIL,
        component: WordDocumentGenerationTemplateDetailSettingsView,
        meta: { title: 'Einstellungen > Dokumentenvorlagendetails' }
      },
      {
        path: ROUTES.APP.SETTINGS.AI.BASE_SETTINGS,
        component: AiBaseSettingsView,
        meta: { title: 'Einstellungen > KI > Grundeinstellungen' }
      },
      {
        path: ROUTES.APP.SETTINGS.AI.ENTITY_EXTRACTION,
        component: AiEntityExtractionSettingsView,
        meta: { title: 'Einstellungen > KI > Beteiligtenextraktion' }
      },
      {
        path: ROUTES.APP.SETTINGS.AI.AI_ASSISTANT_BETA,
        component: AiAssistantBetaSettingsView,
        meta: { title: 'Einstellungen > KI > KI-Assistent (Beta)' }
      },
      {
        path: ROUTES.APP.SETTINGS.AI.PROMPT_TEMPLATES,
        component: PromptTemplateSettingsView,
        meta: { title: 'Einstellungen > KI > Promptvorlagen' }
      },
      {
        path: ROUTES.APP.SETTINGS.AI.PROMPT_TEMPLATE_DETAIL,
        component: PromptTemplateDetailSettingsView,
        meta: { title: 'Einstellungen > KI > Promptvorlage' }
      },

      {
        path: ROUTES.APP.SETTINGS.AI.CASE_LIFECYCLE_WORKFLOWS,
        component: CaseLifecycleWorkflowSettingsView,
        meta: { title: 'Einstellungen > KI > KI Workflows' }
      },
      {
        path: ROUTES.APP.SETTINGS.AI.CASE_LIFECYCLE_WORKFLOWS_BASIC_SETTINGS,
        component: CaseLifecycleWorkflowBasicSettingsView,
        meta: { title: 'Einstellungen > KI > Workflow Grundeinstellungen' }
      },
      {
        path: ROUTES.APP.SETTINGS.AI.AI_AUTONOMY,
        component: AiAutonomySettingsView,
        meta: { title: 'Einstellungen > KI > KI Autonomie' }
      },
      {
        path: ROUTES.APP.SETTINGS.AI.AI_NOTIFICATIONS,
        component: AiNotificationsSettingsView,
        meta: { title: 'Einstellungen > KI > KI Benachrichtigungen' }
      },
      {
        path: ROUTES.APP.SETTINGS.AI_PHONE_ANSWERING.PHONE_NUMBERS,
        component: PhoneNumberSettingsView,
        meta: { title: 'Einstellungen > KI-Anrufbeantworter > Telefonnummern' }
      },
      {
        path: ROUTES.APP.SETTINGS.AI_PHONE_ANSWERING.PHONE_NUMBERS_DETAIL,
        component: PhoneNumberDetailSettingsView,
        meta: { title: 'Einstellungen > KI-Anrufbeantworter > Telefonnummer Details' }
      },
      {
        path: ROUTES.APP.SETTINGS.AI_PHONE_ANSWERING.PHONE_FORWARDING_RULES,
        component: PhoneForwardingRuleSettingsView,
        meta: { title: 'Einstellungen > KI-Anrufbeantworter > Anrufweiterleitungsregeln' }
      },
      {
        path: ROUTES.APP.SETTINGS.AI_PHONE_ANSWERING.PHONE_FORWARDING_RULE_EDIT,
        component: PhoneForwardingRuleEditSettingsView,
        meta: { title: 'Einstellungen > KI-Anrufbeantworter > Regel bearbeiten' }
      },
      {
        path: ROUTES.APP.SETTINGS.LAW_OFFICE_SOFTWARE.ACTAPORT,
        component: ActaportSettingsView,
        meta: { title: 'Einstellungen > Kanzleisoftware > Actaport' }
      },
      {
        path: ROUTES.APP.SETTINGS.LAW_OFFICE_SOFTWARE.ADVOLUX,
        component: AdvoluxSettingsView,
        meta: { title: 'Einstellungen > Kanzleisoftware > Advolux' }
      },
      {
        path: ROUTES.APP.SETTINGS.LAW_OFFICE_SOFTWARE.ADVOWARE,
        component: AdvowareSettingsView,
        meta: { title: 'Einstellungen > Kanzleisoftware > Advoware' }
      },
      {
        path: ROUTES.APP.SETTINGS.LAW_OFFICE_SOFTWARE.JURNODES,
        component: JurnodesSettingsView,
        meta: { title: 'Einstellungen > Kanzleisoftware > Jurnodes' }
      },
      {
        path: ROUTES.APP.SETTINGS.LAW_OFFICE_SOFTWARE.KLEOS,
        component: KleosSettingsView,
        meta: { title: 'Einstellungen > Kanzleisoftware > Kleos' }
      },
      {
        path: ROUTES.APP.SETTINGS.LAW_OFFICE_SOFTWARE.KLEOS_OAUTH,
        component: KleosAuthPopup,
        meta: { title: 'Einstellungen > Kanzleisoftware > Kleos Authentifizierung' }
      },
      {
        path: ROUTES.APP.SETTINGS.LAW_OFFICE_SOFTWARE.LAWFIRM,
        component: LawfirmSettingsView,
        meta: { title: 'Einstellungen > Kanzleisoftware > Lawfirm' }
      },
      {
        path: ROUTES.APP.SETTINGS.LAW_OFFICE_SOFTWARE.RA_MICRO,
        component: RaMicroSettingsView,
        meta: { title: 'Einstellungen > Kanzleisoftware > RA MICRO' }
      },
      {
        path: ROUTES.APP.SETTINGS.LAW_OFFICE_SOFTWARE.DATEV,
        component: DatevSettingsView,
        meta: { title: 'Einstellungen > Kanzleisoftware > DATEV' }
      },
      {
        path: ROUTES.APP.SETTINGS.INTEGRATIONS.WEBHOOKS,
        component: WebhooksSettingsView,
        meta: { title: 'Einstellungen > Integrationen > Webhooks' },
        beforeEnter: async (to, from, next) => {
          const { useUserStore } = await import('@/shared/stores/userStore')
          const { RoleEnum } = await import('@/types/generated')
          const userStore = useUserStore()
          if (!userStore.user) {
            await userStore.fetchUser()
          }
          if (userStore.user?.role === RoleEnum.ADMIN) {
            next()
          } else {
            next({ path: ROUTES.APP.SETTINGS.ROOT })
          }
        }
      },
      {
        path: ROUTES.APP.SETTINGS.INTEGRATIONS.EMAIL_EGRESS,
        component: EmailEgressSettingsView,
        meta: { title: 'Einstellungen > Integrationen > E-Mail Egress' },
        beforeEnter: async (to, from, next) => {
          const { useUserStore } = await import('@/shared/stores/userStore')
          const { RoleEnum } = await import('@/types/generated')
          const userStore = useUserStore()
          if (!userStore.user) {
            await userStore.fetchUser()
          }
          if (userStore.user?.role === RoleEnum.ADMIN) {
            next()
          } else {
            next({ path: ROUTES.APP.SETTINGS.ROOT })
          }
        }
      },
      {
        path: ROUTES.APP.SETTINGS.INTEGRATIONS.HUBSPOT,
        component: HubspotSettingsView,
        meta: { title: 'Einstellungen > Integrationen > HubSpot' },
        beforeEnter: async (to, from, next) => {
          const { useUserStore } = await import('@/shared/stores/userStore')
          const { RoleEnum } = await import('@/types/generated')
          const userStore = useUserStore()
          if (!userStore.user) {
            await userStore.fetchUser()
          }
          if (userStore.user?.role === RoleEnum.ADMIN) {
            next()
          } else {
            next({ path: ROUTES.APP.SETTINGS.ROOT })
          }
        }
      },
      {
        path: ROUTES.APP.SETTINGS.INTEGRATIONS.HIBP,
        component: HibpSettingsView,
        meta: { title: 'Einstellungen > Integrationen > Have I Been Pwned' },
        beforeEnter: async (to, from, next) => {
          const { useUserStore } = await import('@/shared/stores/userStore')
          const { RoleEnum } = await import('@/types/generated')
          const userStore = useUserStore()
          if (!userStore.user) {
            await userStore.fetchUser()
          }
          if (userStore.user?.role === RoleEnum.ADMIN) {
            next()
          } else {
            next({ path: ROUTES.APP.SETTINGS.ROOT })
          }
        }
      },
      {
        path: ROUTES.APP.SETTINGS.INTEGRATIONS.IUSTA,
        component: IustaSettingsView,
        meta: { title: 'Einstellungen > Integrationen > Iusta' },
        beforeEnter: async (to, from, next) => {
          const { useUserStore } = await import('@/shared/stores/userStore')
          const { RoleEnum } = await import('@/types/generated')
          const userStore = useUserStore()
          if (!userStore.user) {
            await userStore.fetchUser()
          }
          if (userStore.user?.role === RoleEnum.ADMIN) {
            next()
          } else {
            next({ path: ROUTES.APP.SETTINGS.ROOT })
          }
        }
      },
      {
        path: ROUTES.APP.SETTINGS.USER.PROFILE,
        component: UserProfileSettingsView,
        meta: { title: 'Einstellungen > Benutzerprofil' }
      },
      {
        path: ROUTES.APP.SETTINGS.USER.PASSWORD,
        component: UserPasswordSettingsView,
        meta: { title: 'Einstellungen > Passwort ändern' }
      },
      {
        path: ROUTES.APP.SETTINGS.DOCUMENT_TEMPLATES,
        component: DocumentTemplatesView,
        meta: { title: 'Einstellungen > Dokumentenvorlagen' }
      },
      {
        path: ROUTES.APP.SETTINGS.CLIENT_ONBOARDING.ONBOARDING_PACKAGE_DETAIL,
        name: 'Settings Onboarding Package Detail',
        component: OnboardingPackageDetailView,
        meta: { title: 'Einstellungen > Onboarding-Paketdetails' }
      },
      {
        path: ROUTES.APP.SETTINGS.CLIENT_ONBOARDING.QUESTIONNAIRE_DETAIL,
        name: 'Settings Client Onboarding Questionnaire Detail',
        component: QuestionnaireDetailView,
        meta: { title: 'Einstellungen > Fragebogendetails' }
      },
      // Default redirect to the first child path
      { path: ROUTES.APP.SETTINGS.ROOT, redirect: ROUTES.APP.SETTINGS.EMAILS.JUPUS_EMAIL },
      // Lawfirm settings
      {
        path: ROUTES.APP.SETTINGS.LAWFIRM.LAWFIRMDATA,
        component: LawFirmDataView
      },
      {
        path: ROUTES.APP.SETTINGS.LAWFIRM.BRANDING,
        component: LawfirmBrandingView
      },
      {
        path: ROUTES.APP.SETTINGS.LAWFIRM.TEAM,
        component: LawfirmEmployeeManagementView
      }
    ]
  },
  // Employee creation standalone route (outside of SettingsBase for full-screen layout)
  {
    path: ROUTES.APP.SETTINGS.LAWFIRM.TEAM_CREATE,
    component: LawfirmEmployeeCreateView,
    meta: { title: 'Mitarbeiter erstellen' }
  },
  // Employee edit standalone route (outside of SettingsBase for full-screen layout)
  {
    path: ROUTES.APP.SETTINGS.LAWFIRM.TEAM_EDIT,
    component: LawfirmEmployeeEditView,
    meta: { title: 'Mitarbeiter bearbeiten' }
  },
  // PDF Form Template standalone routes (outside of SettingsBase for full-screen layout)
  {
    path: ROUTES.APP.SETTINGS.PDF_FORM_TEMPLATES.PDF_FORM_TEMPLATES_CREATE,
    component: PDFFormTemplateCreateView,
    meta: { title: 'PDF-Formularvorlage erstellen' }
  },
  {
    path: ROUTES.APP.SETTINGS.PDF_FORM_TEMPLATES.PDF_FORM_TEMPLATES_EDIT,
    component: PDFFormTemplateEditView,
    meta: { title: 'PDF-Formularvorlage bearbeiten' }
  },
  {
    path: ROUTES.APP.CALLS.ROOT,
    name: 'Calls',
    children: [
      {
        path: ROUTES.APP.CALLS.ROOT,
        name: 'Calls View',
        component: CallListView,
        meta: {
          title: 'Anrufe'
        },
        beforeEnter: async (to, from, next) => {
          const callListImprovementsFlag = useFeatureFlag(FlagEnum.CALL_LIST_IMPROVEMENTS)
          await callListImprovementsFlag.checkFlag()

          if (callListImprovementsFlag.isEnabled.value) {
            to.matched[to.matched.length - 1].components!.default = CallListViewImproved
          }

          next()
        }
      },
      {
        path: ROUTES.APP.CALLS.CALL_DETAIL,
        name: 'Call Detail',
        component: CallDetailView,
        meta: {
          title: 'Anrufdetails'
        }
      }
    ]
  },
  {
    path: ROUTES.APP.ACTIVITIES.ROOT,
    name: 'Activities',
    component: ActivityListView,
    meta: {
      title: 'Aktivitäten'
    }
  },
  {
    path: ROUTES.APP.GLOBAL_CHAT.ROOT,
    name: 'Allgemeiner KI-Chat',
    beforeEnter: async (to, from, next) => {
      // Check if law specific data is enabled for the organization (same condition as case AI chat)
      const { useUserStore } = await import('@/shared/stores/userStore')
      const userStore = useUserStore()

      // Fetch user if not already loaded
      if (!userStore.user) {
        await userStore.fetchUser()
      }

      if (!userStore.user?.org?.enable_law_specific_data) {
        return next({ path: '/cases' })
      }

      next()
    },
    children: [
      {
        path: ROUTES.APP.GLOBAL_CHAT.ROOT,
        name: 'global-chat',
        component: GlobalChatView,
        meta: {
          title: 'Allgemeiner KI-Chat'
        }
      }
    ]
  },
  {
    path: ROUTES.APP.AI_ASSISTANT.ROOT,
    name: 'AI Assistant',
    beforeEnter: async (to, from, next) => {
      const floatingAiButton = useFeatureFlag(FlagEnum.FLOATING_AI_BUTTON)
      await floatingAiButton.checkFlag()
      if (!floatingAiButton.isEnabled.value) {
        return next({ path: ROUTES.APP.GLOBAL_CHAT.ROOT })
      }

      const { useUserStore } = await import('@/shared/stores/userStore')
      const userStore = useUserStore()

      if (!userStore.user) {
        await userStore.fetchUser()
      }

      if (!userStore.user?.org?.enable_law_specific_data) {
        return next({ path: '/cases' })
      }

      const { useUnifiedChatStore } = await import('@/store/app/unifiedChat')
      const unifiedStore = useUnifiedChatStore()
      // Skip the fresh-chat entry path when the navigation was triggered by expanding the
      // side panel — the fullscreen view will pick up ``pendingFullscreenSessionUuid`` and
      // load that session on mount instead.
      if (!unifiedStore.pendingFullscreenSessionUuid) {
        await unifiedStore.startNewFullscreenChat()
      }

      next()
    },
    children: [
      {
        path: ROUTES.APP.AI_ASSISTANT.ROOT,
        name: 'ai-assistant-chat',
        component: AiAssistantChatView,
        meta: {
          title: 'AI Assistant'
        }
      }
    ]
  },
  {
    path: ROUTES.APP.SETTINGS.QUESTIONNAIRES.QUESTIONNAIRE_DETAIL,
    name: 'Settings Questionnaire Detail',
    component: QuestionnaireDetailView,
    meta: { title: 'Einstellungen > Fragebogendetails' }
  },
  {
    path: ROUTES.APP.ONBOARDING.COUNTDOWN,
    name: 'OnboardingCountdown',
    component: OnboardingCountdownView,
    meta: { title: 'Willkommen bei JUPUS' }
  }
]

const clientPortalRoutesOld: RouteRecordRaw[] = [
  // {
  //   path: '',
  //   name: 'ClientPortalHome',
  //   component: ClientPortalView,
  //   meta: {
  //     title: 'Ihre Anfrage'
  //   }
  // },
  // {
  //   path: 'client',
  //   name: 'Home',
  //   component: ClientPortalView,
  //   meta: {
  //     title: 'Ihre Anfrage'
  //   }
  // },
  // {
  //   path: 'questionnaire/:id',
  //   name: 'Questionnaires',
  //   component: QuestionnaireView,
  //   meta: {
  //     title: 'Fragebogen'
  //   }
  // },
  // {
  //   path: 'document/:id',
  //   name: 'PDF Documents',
  //   component: PdfView,
  //   meta: {
  //     title: 'Dokument'
  //   }
  // },
  // {
  //   path: 'external/document/:id',
  //   name: 'External PDF Documents',
  //   component: PdfView,
  //   meta: {
  //     title: 'Dokument'
  //   }
  // },
  // {
  //   path: 'external/pdf/:doc_creation_token/:case_uuid',
  //   name: 'PDF External Redirect',
  //   component: PdfExternalRedirectView,
  //   meta: {
  //     title: 'Dokument'
  //   }
  // },
  // {
  //   path: 'questionnaire/preview/:uuid',
  //   name: 'Questionnaires Preview',
  //   component: QuestionnairePreviewView,
  //   meta: {
  //     title: 'Fragebogen'
  //   }
  // },
  // {
  //   path: 'external/questionnaire/:org_questionnaire_creation_token/:questionnaire_questionnaire_creation_token',
  //   name: 'Questionnaires External Redirect',
  //   component: QuestionnaireExternalRedirectView,
  //   meta: {
  //     title: 'Fragebogen'
  //   }
  // },
  // {
  //   path: 'external/questionnaire/:id',
  //   name: 'Questionnaires External ',
  //   component: QuestionnaireExternalView,
  //   meta: {
  //     title: 'Fragebogen'
  //   }
  // },
  // {
  //   path: 'external/questionnaire/completed',
  //   name: 'Questionnaires External Completed',
  //   component: ExternalQuestionnaireCompletedView,
  //   meta: {
  //     title: 'Fragebogen'
  //   }
  // },
  {
    path: 'call/feedback/:id',
    name: 'Voice Bot Feedback',
    component: VoiceBotFeedback,
    meta: {
      title: 'Anruf Feedback'
    }
  }
]

const clientPortalRoutes = [
  {
    path: ROUTES.CLIENT_PORTAL.ONBOARDING_REDIRECT,
    name: 'Onboarding Redirect',
    component: ClientPortalOnboardingRedirectView,
    meta: { title: 'Onboarding' }
  },
  {
    path: ROUTES.CLIENT_PORTAL.EXTERNAL_QUESTIONNAIRE_REDIRECT,
    name: 'External Questionnaire Redirect',
    component: ClientPortalQuestionnaireExternalRedirectView,
    meta: { title: 'Fragebogen' }
  },
  {
    path: ROUTES.CLIENT_PORTAL.EXTERNAL_QUESTIONNAIRE_REDIRECT_SINGULAR,
    name: 'External Questionnaire Redirect Singular',
    component: ClientPortalQuestionnaireExternalRedirectView,
    meta: { title: 'Fragebogen' }
  },
  {
    path: ROUTES.CLIENT_PORTAL.HOME,
    name: 'ClientPortal',
    component: ClientPortalWrapper,
    meta: {
      title: 'Mandantenportal'
    },
    children: [
      {
        path: ROUTES.CLIENT_PORTAL.HOME,
        name: 'Home',
        component: ClientPortalHomeView,
        meta: { title: 'Startseite' }
      },
      {
        path: ROUTES.CLIENT_PORTAL.QUESTIONNAIRE_DETAIL,
        name: 'Client Portal Questionnaire Detail',
        component: ClientPortalQuestionnaireDetailView,
        meta: { title: 'Fragebogen' }
      },
      {
        path: ROUTES.CLIENT_PORTAL.EXTERNAL_QUESTIONNAIRE_DETAIL,
        name: 'External Questionnaire Detail',
        component: ClientPortalQuestionnaireExternalView,
        meta: { title: 'Fragebogen' }
      },
      {
        path: ROUTES.CLIENT_PORTAL.QUESTIONNAIRE_PREVIEW,
        name: 'Questionnaire Preview',
        component: ClientPortalQuestionnairePreviewView,
        meta: { title: 'Fragebogen Vorschau' }
      },
      {
        path: ROUTES.CLIENT_PORTAL.PDF_TEMPLATE_DETAIL,
        name: 'PDF Template Detail',
        component: ClientPortalPdfTemplateDetailView,
        meta: { title: 'PDF' }
      },
      {
        path: ROUTES.CLIENT_PORTAL.EXTERNAL_PDF_TEMPLATE_DETAIL,
        name: 'External PDF Template Detail',
        component: ClientPortalPdfTemplateDetailView,
        meta: { title: 'PDF' }
      },
      {
        path: ROUTES.CLIENT_PORTAL.PDF_FORM_SUBMISSION_DETAIL,
        name: 'PDF Form Submission Detail',
        component: ClientPortalPDFFormView,
        meta: { title: 'PDF Formular' }
      },
      {
        path: ROUTES.CLIENT_PORTAL.EXTERNAL_COMPLETED,
        name: 'External Completed',
        component: ClientPortalExternalCompletedView,
        meta: { title: 'Fragebogen Abgeschlossen' }
      },
      {
        path: ROUTES.CLIENT_PORTAL.EXTERNAL_PDF_COMPLETED,
        name: 'External PDF Completed',
        component: ClientPortalExternalCompletedView,
        meta: { title: 'PDF Abgeschlossen' }
      }
    ]
  }
]

// Combine the routes
const routes: RouteRecordRaw[] = [
  {
    path: ROUTE_BASES.CLIENT_PORTAL_BASE,
    children: clientPortalRoutes
  },
  {
    path: ROUTE_BASES.APP_BASE,
    children: appRoutes
  }
]

const router = createRouter({
  // Use '/' instead of BASE_URL because assets are served from a CDN with a different base path
  history: createWebHistory('/'),
  routes: routes
})

// Navigation guard for onboarding countdown
router.beforeEach(async (to, from, next) => {
  // Skip onboarding check for admin routes, onboarding routes, public routes, and non-app routes
  const isAdminRoute = to.path.startsWith('/admin')
  const isOnboardingRoute = to.path.startsWith('/app/onboarding')
  const isAppRoute = to.path.startsWith('/app')
  const isPublicRoute = to.meta?.public === true

  if (!isAdminRoute && !isOnboardingRoute && !isPublicRoute && isAppRoute) {
    // Import userStore lazily to avoid circular dependencies
    const { useUserStore } = await import('@/store/userStore')
    const userStore = useUserStore()

    // Ensure user data is loaded
    if (!userStore.user) {
      try {
        await userStore.fetchUser()
      } catch (error) {
        // If fetching user fails, proceed anyway (auth middleware will handle it)
        console.error('Failed to fetch user in route guard:', error)
      }
    }

    // Check if onboarding countdown should be shown
    if (userStore.user?.org?.onboarding_start_date) {
      const onboardingDate = new Date(userStore.user.org.onboarding_start_date)
      const now = new Date()

      // If onboarding date is in the future, redirect to countdown
      if (onboardingDate > now && to.path !== ROUTES.APP.ONBOARDING.COUNTDOWN) {
        return next(ROUTES.APP.ONBOARDING.COUNTDOWN)
      }
    }
  }

  // Allow access to countdown page if coming from the check above
  // or if the onboarding date has passed/is not set
  next()
})

// Navigation guard to handle meta titles
router.beforeEach((to, from, next) => {
  // Get the matched route with the deepest meta title
  const nearestWithTitle = to.matched
    .slice()
    .reverse()
    .find((r) => r.meta?.title)

  // Set document title
  if (nearestWithTitle) {
    document.title = `${nearestWithTitle.meta.title} - Jupus`
  } else {
    document.title = 'Jupus'
  }

  // Proceed with navigation
  next()
})

/**
 * Helper function to check if a path is a case detail route.
 * Used for WebSocket lifecycle management.
 */
function isCaseDetailRoute(path: string): boolean {
  if (!path) return false

  // Match patterns: /app/cases/:uuid/* or /app/cases/legacy/:uuid or /cases/:uuid/* or /cases/legacy/:uuid
  const UUID_REGEX = /[a-f0-9-]{36}/

  return (
    path.match(new RegExp(`^/app/cases/${UUID_REGEX.source}(/|$)`)) !== null ||
    path.match(new RegExp(`^/app/cases/legacy/${UUID_REGEX.source}`)) !== null ||
    path.match(new RegExp(`^/cases/${UUID_REGEX.source}(/|$)`)) !== null ||
    path.match(new RegExp(`^/cases/legacy/${UUID_REGEX.source}`)) !== null
  )
}

/**
 * Helper function to disconnect WebSocket from case detail store.
 * Used by both router guard and beforeunload handler.
 */
function disconnectCaseWebSocket(): void {
  import('@/store/app/case/detail/caseDetailStore')
    .then(({ useCaseStore }) => {
      const caseStore = useCaseStore()
      caseStore.disconnectWebSocket()
    })
    .catch((error) => {
      console.warn('Could not disconnect WebSocket:', error)
    })
}

// Navigation guard to manage WebSocket lifecycle for case detail views
router.afterEach((to, from) => {
  const wasOnCaseDetail = isCaseDetailRoute(from.path)
  const isOnCaseDetail = isCaseDetailRoute(to.path)

  // If we're leaving case detail routes entirely, disconnect WebSocket
  if (wasOnCaseDetail && !isOnCaseDetail) {
    disconnectCaseWebSocket()
  }

  // Initialize Preline components
  setTimeout(() => {
    window.HSStaticMethods.autoInit()
  }, 100)
})

// Handle cleanup when leaving the SPA entirely (e.g., navigating to Django routes like /cases/)
// This catches full page navigations that the Vue Router guard can't catch
window.addEventListener('beforeunload', () => {
  const currentPath = window.location.pathname

  if (isCaseDetailRoute(currentPath)) {
    // Note: beforeunload is best-effort, browser may not wait for async operations
    disconnectCaseWebSocket()
  }
})

export default router
